import React, { createContext, useEffect, useState, useReducer, useContext, useCallback, useRef } from 'react'
import { getCachedAuthSession } from 'services/authSessionCache'
import PropTypes from 'prop-types'
import Environment from 'helpers/environment'

const WebsocketContext = createContext()

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.shape()
  ]).isRequired
}

export const WebsocketContextProvider = ({ children }) => {
  const clientRef = useRef(null)
  const [closed, forceClose] = useReducer(() => true, false)
  const [payloads, setPayloads] = useState({})
  const subscriptionsRef = useRef(new Set())
  const heartbeatIntervalRef = useRef(null)

  useEffect(() => {
    initializeClient()
    return () => {
      if (clientRef.current != null) {
        forceClose()
        clientRef.current.close()
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const unsubscribe = useCallback((entityId, topic) => {
    const subscriptionKey = `${entityId}-${topic}`
    subscriptionsRef.current.delete(subscriptionKey)

    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      clientRef.current.send(JSON.stringify({
        'operation': 'unsubscribe',
        topic,
        entityId
      }))
    }
  }, [])

  const subscribe = useCallback(async (entityId, topic) => {
    const currentSession = await getCachedAuthSession()
    const idToken = currentSession.tokens?.idToken
    const subscriptionKey = `${entityId}-${topic}`
    subscriptionsRef.current.add(subscriptionKey)

    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      clientRef.current.send(JSON.stringify({
        'operation': 'subscribe',
        topic,
        entityId,
        'idToken': idToken.toString()
      }))
    } else {
      console.log('WebSocket not open, subscription queued for reconnect:', subscriptionKey)
    }
  }, [])

  const sendHeartbeat = useCallback(() => {
    if (clientRef.current != null && clientRef.current.readyState === WebSocket.OPEN) {
      console.log('Sending heartbeat')
      clientRef.current.send(JSON.stringify({
        'operation': 'heartbeat'
      }))
    }
  }, [])

  const initializeClient = async () => {
    const currentSession = await getCachedAuthSession()
    const idToken = currentSession.tokens?.idToken
    if (!idToken) {
      return false
    }

    const newClient = new WebSocket(`${Environment.webSocketURL()}?idToken=${idToken}`)

    newClient.onopen = () => {
      console.log('WebSocket connected')
      console.log('Resubscribing to', subscriptionsRef.current.size, 'subscriptions')

      // Resubscribe to all active subscriptions using the ref
      if (newClient.readyState === WebSocket.OPEN) {
        subscriptionsRef.current.forEach((subscription) => {
          const [entityId, topic] = subscription.split('-')
          console.log('Resubscribing to:', entityId, topic)
          newClient.send(JSON.stringify({
            'operation': 'subscribe',
            topic,
            entityId,
            'idToken': idToken.toString()
          }))
        })
      }
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, 1000 * 60 * 3) // 3 minutes
    }

    newClient.onerror = () => {
      console.log('WebSocket error:')

      setTimeout(async () => {
        try {
          await initializeClient()
        } catch (error) {
          console.log('Failed to reinitialize WebSocket:', error)
        }
      }, 1000)
    }

    newClient.onclose = () => {
      console.log('WebSocket closed')
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
      if (!closed) {
        setTimeout(async () => {
          try {
            await initializeClient()
          } catch (error) {
            console.log('Failed to reinitialize WebSocket:', error)
          }
        }, 1000)
      }
    }

    newClient.onmessage = async (message) => {
      setPayloads((prevPayloads) => {
        const updatedPayloads = { ...prevPayloads }
        const data = JSON.parse(message.data)
        console.log('WebSocket message received:', data)
        updatedPayloads[`${data.deviceId}-${data.topic}`] = data
        return updatedPayloads
      })
    }

    clientRef.current = newClient
  }

  return (
    <WebsocketContext.Provider value={{ subscribe, payloads, unsubscribe }}>
      {children}
    </WebsocketContext.Provider>
  )
}

WebsocketContextProvider.propTypes = propTypes

export const useWebsocketContext = (entityId, topic) => {
  const context = useContext(WebsocketContext)

  if (!entityId || !topic) {
    return context
  }

  const payloadKey = `${entityId}-${topic}`
  const filteredPayload = context.payloads[payloadKey] ?? null

  return {
    ...context,
    payload: filteredPayload
  }
}
